import JobStore from 'jobs/JobStore'
import SummaryStore from 'jobs/SummaryStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import CouponStore from 'billing/CouponStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { Subscription } from 'rxjs'
import BillingStore from 'billing/BillingStore'
import BillingMethod from 'billing/models/BillingMethod'
import Workspace from 'iam/models/Workspace'
import CardPaymentMethodFormViewModel from './Payment/CardPaymentMethodForm/CardPaymentMethodFormViewModel'
import { CardPaymentMethodEditorPorts } from 'billing/payment-methods/ports'
import { Stripe, loadStripe } from '@stripe/stripe-js'
import JobCheckoutViewModel from './JobCheckoutViewModel'

export class BillingMethodViewModel {
  @observable selected = false

  constructor(
    private billingMethod: BillingMethod,
    private workspace: Workspace,
    private onSelect: (billingMethod: BillingMethod) => void,
  ) {
    makeObservable(this)
  }

  @computed
  get id() {
    return this.billingMethod.id
  }

  @computed
  get brand() {
    return this.billingMethod.brand
  }

  @computed
  get last4() {
    return this.billingMethod.last4
  }

  @computed
  get expires() {
    return `${this.billingMethod.expMonth}/${this.billingMethod.expYear}`
  }

  @computed
  get title() {
    return `${this.workspace.name} Credit`
  }

  @computed
  get type() {
    return this.billingMethod.type
  }

  @action.bound
  setSelected(selected: boolean) {
    this.selected = selected
  }

  @action
  toggleSelected() {
    this.onSelect(this.billingMethod)
  }
}

export default class CheckoutViewModel {
  /**
   * Manages an Rx subscription while the VM is active.
   */
  private subscription = new Subscription()

  /**
   * Stripe instance.
   */
  stripe: Stripe | null = null

  @observable selectedPaymentMethod: BillingMethod | null = null
  @observable jobCheckoutVMs: JobCheckoutViewModel[] = []

  /**
   * View Model for card payment form.
   */
  @observable cardPaymentMethodFormVM: CardPaymentMethodFormViewModel | null =
    null

  constructor(
    private summaryStore: SummaryStore,
    private jobStore: JobStore,
    private publishedLegendStore: PublishedLegendStore,
    private couponStore: CouponStore,
    private billingStore: BillingStore,
    private cardPaymentMethodEditorAdapter: CardPaymentMethodEditorPorts,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Fetch the information necessary for the checkout.
   */
  fetchCheckoutInfo = task.resolved(async (id: Id[]) => {
    this.jobCheckoutVMs = id.map(
      (i) =>
        new JobCheckoutViewModel(
          i,
          this.summaryStore,
          this.jobStore,
          this.publishedLegendStore,
          this.couponStore,
          this.newAlert,
        ),
    )

    if (this.jobCheckoutVMs.length === 0) {
      return
    }

    await Promise.all(this.jobCheckoutVMs.map((vm) => vm.fetchJobInfo()))
    await Promise.all([this.fetchPaymentMethods()])

    if (this.member) {
      this.stripe = await this.loadStripeInstance()
      this.cardPaymentMethodFormVM = new CardPaymentMethodFormViewModel(
        this.member,
        () => this.pay(),
        this.cardPaymentMethodEditorAdapter,
        this.stripe,
        this,
      )
    }
  })

  @computed
  get checkoutCompleted() {
    return this.jobCheckoutVMs.every((vm) => vm.checkoutCompleted)
  }

  @computed
  get member() {
    return this.jobCheckoutVMs[0]?.job?.championClient?.member
  }

  @computed
  get userPublicId() {
    return this.jobCheckoutVMs[0]?.job?.championClient?.member.user.publicId
  }

  @computed
  get workspace() {
    return this.jobCheckoutVMs[0]?.job?.workspace
  }

  /**
   * List of existing payment methods.
   */
  @computed
  get paymentMethods() {
    const billingProfile = this.billingStore.billingProfiles.find(
      (p) =>
        p.userId === this.member?.user.id && p.workspace === this.workspace,
    )

    return billingProfile?.billingMethods.map(
      (bm) =>
        new BillingMethodViewModel(
          bm,
          this.workspace,
          this.setSelectedPaymentMethod,
        ),
    )
  }

  /**
   * Wether or not the client has payment methods.
   */
  @computed
  get hasPaymentMethods() {
    return this.paymentMethods && this.paymentMethods.length > 0
  }

  /**
   * The total amount remaining to be paid.
   */
  @computed
  get total() {
    return this.jobCheckoutVMs.reduce((acc, vm) => {
      return vm.checkoutCompleted ? acc + 0 : acc + vm.amountDueNow
    }, 0)
  }

  /**
   * Fetch existing payment methods.
   */
  @action
  async fetchPaymentMethods() {
    await this.billingStore.fetchBillingProfileForIndividual(
      this.workspace.id,
      this.userPublicId,
    )
  }

  /**
   * Sets the selected payment method.
   * @param billingMethod
   */
  @action.bound
  setSelectedPaymentMethod(billingMethod: BillingMethod) {
    this.selectedPaymentMethod =
      billingMethod === this.selectedPaymentMethod ? null : billingMethod

    this.paymentMethods?.forEach((pm) =>
      pm.setSelected(pm.id === this.selectedPaymentMethod?.id),
    )
  }

  /**
   * Wether or not a payment method is selected.
   * @param billingMethod
   * @returns
   */
  isSelectedPaymentMethod(billingMethod: BillingMethod) {
    return billingMethod.id === this.selectedPaymentMethod?.id
  }

  /**
   * Submit the job with the selected payment method.
   * @returns
   */
  @action.bound
  async pay() {
    const alert = this.newAlert()
      .withState(AlertState.Pending)
      .saying('Submitting jobs', 'Hang tight!')
      .show()
      .autoDismiss()

    let paymentMethod: BillingMethod | null = this.selectedPaymentMethod

    try {
      // If the admin doesn't select one of the existing payment methods
      // try to add a new one with the card form.
      if (!paymentMethod && this.cardPaymentMethodFormVM) {
        const paymentMethodResult =
          await this.cardPaymentMethodFormVM.savePaymentMethod()

        if (paymentMethodResult.isErr()) {
          alert
            .withState(AlertState.Error)
            .saying('Failed!', paymentMethodResult.unwrapErr())
            .autoDismiss()
          return
        }
        paymentMethod = paymentMethodResult.unwrap()
      }

      await Promise.all(this.jobCheckoutVMs.map((vm) => vm.pay(paymentMethod)))

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'All jobs have been submitted!')
        .autoDismiss()
    } catch (e) {
      alert
        .withState(AlertState.Error)
        .saying('Failed!', 'Could not submit some jobs.')
        .autoDismiss()
    }
  }

  @action
  clearSelectedPaymentMethod() {
    this.selectedPaymentMethod = null
  }

  /**
   * Loads the stripe instance.
   * @returns
   */
  async loadStripeInstance() {
    const key = this.cardPaymentMethodEditorAdapter.getStripePublishableKey()
    if (!key)
      throw new Error(
        'There is a missing Billing configuration. Please contact an administrator.',
      )

    return await loadStripe(key)
  }

  activate = task.resolved(async () => {})

  deactivate() {
    this.subscription.unsubscribe()
    this.clearSelectedPaymentMethod()
  }
}
